import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useDeletePackage,
  useGetPackages,
  useUpdatePackageStatus,
} from "../api-clients";
import usePackagesTableColumns from "./usePackagesTableColumns";

const PackagesList = () => {
  useSetActiveTab(TAB_NAMES.PACKAGE_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updatePackageData,
    isValidating,
  } = useGetPackages({ length, page, search: debouncedSearch });

  const { isRefreshing, handleRefresh } = useManualRefresh(updatePackageData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deletePackage,
  } = useDeletePackage();

  const packagesData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const {
    execute: handlePackageStatusChange,
    isSuccess: isStatusUpdateSuccess,
  } = useUpdatePackageStatus();

  const columns = usePackagesTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
    handlePackageStatusChange,
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updatePackageData();
      setIsDeleteModalOpen(false);
      successNotification("Package Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updatePackageData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  useEffect(() => {
    if (isStatusUpdateSuccess) {
      updatePackageData();
    }
  }, [isStatusUpdateSuccess, updatePackageData]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deletePackage(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Package Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(packagesData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Package",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={packagesData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default PackagesList;
